import { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    backgroundColor: "rgba(255,255,255,0.1)",
    "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.35)" },
    "&.Mui-focused fieldset": { borderColor: "#ff9f1c" },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.75)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#ff9f1c" },
  "& .MuiInputBase-input": { color: "white", WebkitTextFillColor: "white", caretColor: "#ff9f1c" },
  "& .MuiSelect-icon": { color: "rgba(255,255,255,0.45)" },
};

function ContactForm() {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    workers: "",
    message: "",
  });

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-contact-form", onOpen);
    return () => window.removeEventListener("open-contact-form", onOpen);
  }, []);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = new FormData(e.target);
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(data).toString(),
    })
      .then(() => setSubmitted(true))
      .catch(() => setSubmitted(true));
  };

  return (
    <>
      {/* Floating trigger button — always fixed bottom-right, always amber */}
      <MKBox
        onClick={() => setOpen(true)}
        sx={{
          position: "fixed",
          bottom: 32,
          right: 32,
          zIndex: 1300,
          opacity: visible && !open ? 1 : 0,
          transform: visible && !open ? "translateY(0) scale(1)" : "translateY(16px) scale(0.92)",
          transition: "opacity 0.3s ease, transform 0.3s ease",
          pointerEvents: visible && !open ? "auto" : "none",
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          background: "#ff9f1c",
          borderRadius: "50px",
          px: 2.5,
          py: 1.25,
          boxShadow: "0 4px 1px grey, 0 2px 8px rgba(0,0,0,0.2)",
          "@keyframes btn-bounce": {
            "0%, 100%": { transform: "translateY(0) scale(1)" },
            "50%": { transform: "translateY(-6px) scale(1.03)" },
          },
          animation: "btn-bounce 2s ease-in-out infinite",
          "&:hover": { animationPlayState: "paused", transform: "translateY(-2px) scale(1.05)" },
          "&:active": { transform: "scale(0.97)" },
        }}
      >
        <Icon sx={{ color: "white", fontSize: "18px !important", opacity: 0.9 }}>edit_note</Icon>
        <MKTypography
          variant="button"
          sx={{
            color: "white",
            fontWeight: 700,
            fontSize: "0.85rem",
            letterSpacing: "0.02em",
            lineHeight: 1,
          }}
        >
          Get Started
        </MKTypography>
      </MKBox>

      {/* Expanded form panel */}
      <MKBox
        sx={{
          position: "fixed",
          bottom: 76,
          right: 32,
          zIndex: 1200,
          width: { xs: "calc(100vw - 48px)", sm: 480 },
          pointerEvents: open ? "auto" : "none",
        }}
      >
        <Collapse in={open} timeout={280} sx={{ overflow: "visible" }}>
          <MKBox
            sx={{
              background: "rgba(18, 20, 26, 0.97)",
              backdropFilter: "blur(16px)",
              borderRadius: "16px",
              border: "1px solid rgba(255,255,255,0.1)",
              borderTop: "2px solid #ff9f1c",
            }}
          >
            {/* Header */}
            <MKBox
              px={3}
              pt={2}
              pb={1.5}
              display="flex"
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <MKBox>
                <MKTypography
                  variant="h6"
                  sx={{ color: "white !important", fontWeight: 700, fontSize: "1rem", mb: 0.5 }}
                >
                  Set up your company account
                </MKTypography>
                <MKTypography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                  We&apos;ll get back to you within one business day.
                </MKTypography>
              </MKBox>
              <MKBox
                onClick={() => setOpen(false)}
                sx={{
                  cursor: "pointer",
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.08)",
                  flexShrink: 0,
                  ml: 2,
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
                }}
              >
                <Icon sx={{ color: "rgba(255,255,255,0.7)", fontSize: "16px !important" }}>
                  close
                </Icon>
              </MKBox>
            </MKBox>

            {/* Divider */}
            <Divider light sx={{ mx: 3, my: 0, opacity: 1 }} />

            {/* Body */}
            <MKBox px={3} pt={1.5} pb={3}>
              {submitted ? (
                <MKBox textAlign="center" py={4}>
                  <MKBox
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      background: "rgba(72,199,142,0.12)",
                      border: "1px solid rgba(72,199,142,0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                    }}
                  >
                    <Icon sx={{ color: "#48c78e", fontSize: "28px !important" }}>check</Icon>
                  </MKBox>
                  <MKTypography
                    variant="h6"
                    sx={{ color: "white !important", fontWeight: 700, mb: 0.75, fontSize: "1rem" }}
                  >
                    Enquiry sent!
                  </MKTypography>
                  <MKTypography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
                    We&apos;ll be in touch within one business day.
                  </MKTypography>
                </MKBox>
              ) : (
                <form
                  name="workforce-enquiry"
                  method="POST"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  onSubmit={handleSubmit}
                >
                  <input type="hidden" name="form-name" value="workforce-enquiry" />
                  <input type="hidden" name="bot-field" />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Company Name"
                        name="companyName"
                        value={form.companyName}
                        onChange={handleChange}
                        required
                        size="small"
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        name="contactName"
                        value={form.contactName}
                        onChange={handleChange}
                        required
                        size="small"
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        size="small"
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        name="phone"
                        type="tel"
                        value={form.phone}
                        onChange={handleChange}
                        size="small"
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MKBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <MKBox
                          component="button"
                          type="button"
                          onClick={() =>
                            setForm((p) => ({ ...p, workers: Math.max(1, (p.workers || 1) - 1) }))
                          }
                          sx={{
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                            border: "1px solid rgba(255,255,255,0.15)",
                            borderRadius: "20px",
                            background: "rgba(255,255,255,0.1)",
                            color: "rgba(255,255,255,0.75)",
                            fontSize: "1rem",
                            cursor: "pointer",
                            transition: "border-color 0.2s, color 0.2s",
                            "&:hover": {
                              borderColor: "rgba(255,255,255,0.35)",
                              color: "white",
                            },
                          }}
                        >
                          −
                        </MKBox>
                        <TextField
                          fullWidth
                          label="Number of Workers"
                          name="workers"
                          type="number"
                          value={form.workers || ""}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              workers:
                                e.target.value === "" ? "" : Math.max(1, Number(e.target.value)),
                            }))
                          }
                          required
                          size="small"
                          inputProps={{ min: 1, style: { textAlign: "center" } }}
                          sx={{
                            ...fieldSx,
                            "& input[type=number]::-webkit-inner-spin-button": { display: "none" },
                            "& input[type=number]::-webkit-outer-spin-button": { display: "none" },
                          }}
                        />
                        <MKBox
                          component="button"
                          type="button"
                          onClick={() => setForm((p) => ({ ...p, workers: (p.workers || 0) + 1 }))}
                          sx={{
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                            border: "1px solid rgba(255,255,255,0.15)",
                            borderRadius: "20px",
                            background: "rgba(255,255,255,0.1)",
                            color: "rgba(255,255,255,0.75)",
                            fontSize: "1rem",
                            cursor: "pointer",
                            transition: "border-color 0.2s, color 0.2s",
                            "&:hover": { borderColor: "rgba(255,255,255,0.35)", color: "white" },
                          }}
                        >
                          +
                        </MKBox>
                      </MKBox>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Message (optional)"
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        multiline
                        rows={3}
                        size="small"
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MKBox
                        component="button"
                        type="submit"
                        sx={{
                          width: "100%",
                          mt: 0.5,
                          py: 1.4,
                          px: 2,
                          border: "none",
                          borderRadius: "10px",
                          cursor: "pointer",
                          background: "#ff9f1c",
                          boxShadow: "0 4px 16px rgba(255,159,28,0.35)",
                          transition: "transform 0.15s ease, box-shadow 0.15s ease",
                          "&:hover": {
                            // transform: "translateY(-1px)",
                            background: "#e07000",
                            // boxShadow: "0 8px 24px rgba(255,159,28,0.5)",
                          },
                          "&:active": { transform: "scale(0.98)" },
                        }}
                      >
                        <MKTypography
                          variant="button"
                          sx={{ color: "white", fontWeight: 700, fontSize: "0.875rem" }}
                        >
                          Send Enquiry
                        </MKTypography>
                      </MKBox>
                    </Grid>
                  </Grid>
                </form>
              )}
            </MKBox>
          </MKBox>
        </Collapse>
      </MKBox>
    </>
  );
}

export default ContactForm;
